/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react';
import { Trans, useTranslation } from 'next-i18next';
import styled, { css } from 'styled-components';
import useMobileView from '@design-system/hooks/useMobileView';
import { CheckCircleFilled } from '@ant-design/icons';
import useIsIOS from '@source/hooks/useIsIOS';
import { TRegionKey } from '@source/interface';

const StyledWrapper = styled.div<{ isMobileIOS: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  ${(props) => props.theme.typo.familyGoogle.regular};
  color: ${(props) => props.theme.color.darkBackground};
  text-align: center;
  padding: 55px 0 16px;
  background-color: ${(props) => props.theme.color.surfaceDark};
  border-radius: 8px;

  @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
    padding: 90px 0;
    gap: 49px;
  }

  .mainTitle {
    font-size: 20px;
    color: ${(props) => props.theme.color.primaryBase};
    ${(props) => props.theme.typo.familyGoogle.semiBold};
    line-height: 25px;

    @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
      font-size: 30px;
      line-height: 35px;
    }
  }

  .subTitle {
    font-size: 9px;
    line-height: 15px;
    margin-top: 20px;
    margin-bottom: 24px;

    @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
      font-size: 14px;
      margin-bottom: 0;
    }
  }

  .tableTitle {
    ${(props) => props.theme.typo.familyGoogle.semiBold};
    font-size: 11px;
    line-height: 17px;
    max-width: 700px;
    margin-bottom: 24px;

    @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
      font-size: 20px;
      line-height: 30px;

      span {
        display: inline;
      }
    }
  }

  .tableWrapper {
    padding: 24px 35px 24px 0;
    background-color: ${(props) => props.theme.color.background};
    border-radius: 15px;
    overflow-x: auto;
    width: 100%;
    box-shadow: ${(props) => props.theme.color.boxShadow.cardRaiseMiddleShadow};
    max-width: 890px;
    margin-inline: 4px;

    @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
      padding: 30px 50px;
      margin-inline: 0;
      display: flex;
      justify-content: center;
    }

    table {
      border-collapse: collapse;
      border-style: hidden;
      position: relative;
      -webkit-overflow-scrolling: touch;

      td,
      th {
        border: 0.5px solid ${(props) => props.theme.color.onBackgroundMediumEmphasis};
        padding: 13px;
        font-size: 8px;
        line-height: 13px;
        ${(props) => props.theme.typo.familyGoogle.regular};
        min-width: 90px;

        @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
          font-size: 18px;
          line-height: 22px;
          padding: 13px 30px;
        }
      }

      td {
        @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
          font-size: 14px;
        }

        @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.xl}) {
          min-width: 260px;
        }
      }

      th {
        padding-top: 0;

        span {
          white-space: nowrap;
        }

        @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.md}) {
          padding-top: 13px;
          max-height: unset;

          ${(props) => props.theme.typo.familyGoogle.semiBold};
        }
      }

      ${(props) =>
        props.isMobileIOS &&
        css`
          border-collapse: separate;
          border-spacing: 0;

          td,
          th {
            border-width: 0.125px !important;
          }

          td {
            border-bottom: 0;
            border-right: 0;
          }

          th {
            border-top: 0;
            border-bottom: 0;
            border-right: 0;
          }

          .mobileTableThead,
          .thead1.mobile {
            border-right: 0.125px solid ${props.theme.color.onBackgroundMediumEmphasis} !important;
          }

          .first {
            border-left: 0;
          }
        `};
    }

    .tableRowLabel {
      text-align: left;
      padding-left: 0;
    }

    .tableHeader {
      ${(props) => props.theme.typo.familyGoogle.semiBold};
      margin-bottom: 5px;
    }

    .tableHeader.mobile {
      font-size: 10px;
      line-height: 22px;
    }

    .tableSubheader {
      ${(props) => props.theme.typo.familyGoogle.regular};
      font-size: 14px;

      &.mobile {
        font-size: 8px;
      }
    }

    .mobileTableThead {
      min-width: 110px;
      padding-left: 0;
      text-align: left;
      border-left: 0;
    }

    .thead1.mobile {
      border: 0;
    }

    .thead1.mobile,
    .mobileTableThead {
      position: sticky;
      left: 35px;
      z-index: 1;
      background: white;
    }

    .fakeTHead {
      width: 35px;
      min-width: 35px;
      border: 0;
      position: sticky;
      left: 0;
      z-index: 2;
      background: white;
    }

    .fakeEndColumn {
      width: 35px;
      border: 0;
      min-width: 0;
    }
  }

  .swipeToSeeMore {
    width: 100%;
    text-align: right;
    font-weight: 200;
    font-style: italic;
    font-size: 8px;
    color: rgba(0, 0, 0, 0.7);
    line-height: 22px;
    margin-top: 6px;
  }

  .checkIcon {
    font-size: 15px;
    color: ${(props) => props.theme.color.primaryBase};

    @media only screen and (min-width: ${(props) => props.theme.metrics.breakpoints.xl}) {
      font-size: 24px;
    }
  }

  b {
    font-weight: 700;
  }
`;

const Icon = <CheckCircleFilled className="checkIcon" />;

const getCCPOLiteComparisonTableContent = (platform: 'desktop' | 'mobile', region: TRegionKey) => ({
  header: {
    main: `ccpo.ccpoLiteComparisonTable.${platform}.title.main`,
    main2nd: `ccpo.ccpoLiteComparisonTable.${platform}.title.main2nd`,
    sub: `ccpo.ccpoLiteComparisonTable.${platform}.title.sub`,
    tableTitle: `ccpo.ccpoLiteComparisonTable.${platform}.title.tableTitle`,
  },
  thead1: {
    main: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.th1`,
    sub: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.th1Sub`,
  },
  thead2: {
    main: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.th2`,
    sub: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.th2Sub`,
  },
  tbody: [
    {
      label: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td1`,
      contentCC: Icon,
      contentCCLite: Icon,
    },
    {
      label: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td2`,
      contentCC: Icon,
      contentCCLite: Icon,
    },
    {
      label: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td3`,
      contentCC: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td4`,
      contentCCLite: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td5`,
    },
    {
      label: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td6`,
      contentCC: Icon,
      contentCCLite: Icon,
    },
    {
      label: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td7`,
      contentCC: Icon,
      contentCCLite: Icon,
    },
    {
      label: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td8`,
      contentCC: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td9`,
      contentCCLite: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td10`,
    },
    {
      label: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td11`,
      contentCC: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td12`,
      contentCCLite: `ccpo.ccpoLiteComparisonTable.${platform}.table.${region}.td13`,
    },
  ],
  swipeToSeeMore: `ccpo.ccpoLiteComparisonTable.${platform}.swipeToSeeMore`,
});

const CCPOLiteComparisonTable = ({ region }: { region: TRegionKey }) => {
  const { t } = useTranslation('ccpo');
  const isMobile = useMobileView(768);
  const isIos = useIsIOS();

  const isMobileIOS = isMobile && isIos;

  const CCPO_LITE_COMPARISON_TABLE_CONTENT = getCCPOLiteComparisonTableContent(isMobile ? 'mobile' : 'desktop', region);

  return (
    <StyledWrapper isMobileIOS={isMobileIOS}>
      <div>
        <h2 className="mainTitle" style={{ margin: '0' }}>
          <Trans
            ns="ccpo"
            defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.header.main)}
            components={{ b: <b />, span: <span />, br: <br /> }}
          />
        </h2>
        <h2 className="mainTitle" style={{ margin: '0' }}>
          <Trans
            ns="ccpo"
            defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.header.main2nd)}
            components={{ span: <span style={{ whiteSpace: 'nowrap' }} />, br: isMobile ? <br /> : <span /> }}
          />
        </h2>

        <p className="subTitle">
          <Trans
            ns="ccpo"
            defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.header.sub)}
            components={{ b: <b />, span: <span />, br: isMobile ? <br /> : <span /> }}
          />
        </p>
      </div>

      <h3 className="tableTitle">
        <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.header.tableTitle)} components={{ b: <b /> }} />
      </h3>

      <div className="tableWrapper">
        {isMobile ? (
          <table>
            <thead>
              <tr>
                <th aria-label="fakeTHead" className="fakeTHead" />
                <th aria-label="thead1" className="thead1 mobile" />
                {CCPO_LITE_COMPARISON_TABLE_CONTENT.tbody.map((item, index) => (
                  <th key={index} className={index === 0 ? 'first' : ''}>
                    <Trans ns="ccpo" defaults={t(item.label)} components={{ b: <b />, span: <span /> }} />
                  </th>
                ))}
                {isMobileIOS && <th aria-label="fakeEndColumn" className="fakeEndColumn" />}
              </tr>
            </thead>
            <tbody>
              <tr>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td aria-label="fakeTHead" className="fakeTHead" />
                <td className="mobileTableThead">
                  <div className="tableHeader mobile">
                    <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.thead1.main)} />
                  </div>

                  <div className="tableSubheader mobile">
                    <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.thead1.sub)} />
                  </div>
                </td>
                {CCPO_LITE_COMPARISON_TABLE_CONTENT.tbody.map((content, index) => (
                  <td key={index} className={index === 0 ? 'first' : ''}>
                    {typeof content.contentCC === 'string' ? (
                      <Trans
                        ns="ccpo"
                        defaults={t(content.contentCC)}
                        components={{ b: <b />, span: <span />, noWrap: <span style={{ whiteSpace: 'nowrap' }} /> }}
                      />
                    ) : (
                      content.contentCC
                    )}
                  </td>
                ))}
              </tr>
              <tr>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <td aria-label="fakeTHead" className="fakeTHead" />
                <td className="mobileTableThead">
                  <div className="tableHeader mobile">
                    <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.thead2.main)} />
                  </div>

                  <div className="tableSubheader mobile">
                    <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.thead2.sub)} />
                  </div>
                </td>
                {CCPO_LITE_COMPARISON_TABLE_CONTENT.tbody.map((content, index) => (
                  <td key={index} className={index === 0 ? 'first' : ''}>
                    {typeof content.contentCCLite === 'string' ? (
                      <Trans
                        ns="ccpo"
                        defaults={t(content.contentCCLite)}
                        components={{ b: <b />, span: <span />, noWrap: <span style={{ whiteSpace: 'nowrap' }} /> }}
                      />
                    ) : (
                      content.contentCCLite
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <th aria-label="thead1" className="thead1" />
                <th>
                  <div className="tableHeader">
                    <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.thead1.main)} />
                  </div>

                  <div className="tableSubheader">
                    <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.thead1.sub)} />
                  </div>
                </th>
                <th>
                  <div className="tableHeader">
                    <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.thead2.main)} />
                  </div>

                  <div className="tableSubheader">
                    <Trans ns="ccpo" defaults={t(CCPO_LITE_COMPARISON_TABLE_CONTENT.thead2.sub)} />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {CCPO_LITE_COMPARISON_TABLE_CONTENT.tbody.map((content, index) => (
                <tr key={index}>
                  <td className="tableRowLabel">
                    <Trans ns="ccpo" defaults={t(content.label)} components={{ b: <b />, span: <span /> }} />
                  </td>
                  <td>
                    {typeof content.contentCC === 'string' ? (
                      <Trans ns="ccpo" defaults={t(content.contentCC)} components={{ b: <b />, span: <span /> }} />
                    ) : (
                      content.contentCC
                    )}
                  </td>
                  <td>
                    {typeof content.contentCCLite === 'string' ? (
                      <Trans ns="ccpo" defaults={t(content.contentCCLite)} components={{ b: <b />, span: <span /> }} />
                    ) : (
                      content.contentCCLite
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {isMobile && <div className="swipeToSeeMore">{t(CCPO_LITE_COMPARISON_TABLE_CONTENT.swipeToSeeMore)}</div>}
    </StyledWrapper>
  );
};

export default CCPOLiteComparisonTable;
